/* driveSteps.js — the browser half of a drive turn.
   ──────────────────────────────────────────────────────────────────────────
   When NoVA DOES a task on the user's screen (agentOS runtime driveTurn),
   the worker writes one step at a time onto the user's aiMessage:

     drive: { seq, name, args, say, pace, at }

   Whichever agent surface sees it first runs it here — the pointer glides,
   the control is pressed or typed into at the given pace, the caption shows
   `say` — then answers on the same doc:

     driveResult: { seq, result, screen, route, at }

   `screen` is the fresh outline (screenOutline.js) so the model reads the
   page it just changed before its next move. The panel and the dock both
   subscribe to the same messages; the module-level `ran` set makes a step
   run exactly once per tab.

   Same file in the control plane and the App Kit foundation — edit both. */

import { runPageControl, setDriveCaption, clearDriveCaption } from './pageControls.js';
import { captureScreenOutline } from './screenOutline.js';

const ran = new Set();
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** The step on `m` that has not been answered yet, or null. */
export function pendingDriveStep(m) {
  if (!m || m.role !== 'user' || m.status !== 'executing') return null;
  const d = m.drive;
  if (!d || !Number(d.seq)) return null;
  if (m.driveResult && Number(m.driveResult.seq) === Number(d.seq)) return null;
  return d;
}

/** True while any turn in the list is being driven on this screen. */
export function isDriving(messages) {
  return (messages || []).some((m) => m.role === 'user' && m.status === 'executing' && (m.drive || m.driveGoal));
}

/* Firestore refuses undefined / functions; the outline is already capped. */
function compact(result) {
  try { return JSON.parse(JSON.stringify(result ?? { ok: true })); } catch (_) { return { ok: true }; }
}

/**
 * runDriveStep(m, { navigate, resolvePath, write })
 *   navigate: react-router navigate · resolvePath: surface's route resolver
 *   write(patch): updates the aiMessage (the only client write besides approval)
 * Returns true when this call ran the step, false when it was already taken.
 */
export async function runDriveStep(m, { navigate, resolvePath, write }) {
  const d = pendingDriveStep(m);
  if (!d) return false;
  const key = `${m.id}:${d.seq}`;
  if (ran.has(key)) return false;
  ran.add(key);
  const { seq, name, args = {}, say, pace } = d;
  setDriveCaption(say || '');
  let result;
  try {
    if (name === 'navigate_app') {
      const to = resolvePath(args.path);
      navigate(to);
      await sleep(700);
      result = { ok: true, navigatedTo: to, message: `Opened ${to}.` };
    } else {
      result = await Promise.resolve(runPageControl(name, args, { pace }));
    }
  } catch (err) {
    result = { ok: false, error: String(err?.message || err) };
  }
  await sleep(300); // let React render what the move caused
  const screen = captureScreenOutline({ maxChars: 3500 }) || '';
  const route = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}`.slice(0, 300) : '';
  try {
    await write({ driveResult: { seq, result: compact(result), screen, route, at: Date.now() } });
  } catch (err) {
    // The worker times the step out and tells the user; nothing else to do here.
    console.warn('[drive] could not answer step', seq, err?.message || err);
  }
  return true;
}

/** The turn ended (done / error): caption off, pointer parked. */
export function endDrive() { clearDriveCaption(); }
